import { useState, useRef } from "react";

export function OptionListInput({
  value,
  onChange,
  maxOptions,
  maxOptionLength,
  disabled,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  maxOptions: number;
  maxOptionLength: number;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const maxReached = value.length >= maxOptions;

  function commit() {
    const t = draft.trim();
    if (!t) return;
    if (t.length > maxOptionLength) return;
    if (value.some((v) => v.toLowerCase() === t.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...value, t]);
    setDraft("");
    inputRef.current?.focus();
  }

  function remove(i: number) {
    const next = value.slice();
    next.splice(i, 1);
    onChange(next);
  }

  return (
    <div className="field" data-testid="option-list">
      <label>
        Options{" "}
        <span className="muted" style={{ textTransform: "none", letterSpacing: 0 }}>
          (required for this method — add at least two)
        </span>
      </label>
      <div className="row">
        {value.map((opt, i) => (
          <span key={opt + i} className="chip" data-testid="option-chip">
            {opt}
            <button
              aria-label={`Remove option ${opt}`}
              onClick={() => remove(i)}
              disabled={disabled}
              type="button"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="row">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          placeholder={
            maxReached
              ? `Maximum ${maxOptions} options`
              : "Type an option and press Enter…"
          }
          disabled={disabled || maxReached}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit();
            } else if (
              e.key === "Backspace" &&
              draft === "" &&
              value.length > 0
            ) {
              remove(value.length - 1);
            }
          }}
          data-testid="option-input"
        />
        <button
          type="button"
          className="btn"
          onClick={commit}
          disabled={disabled || !draft.trim() || maxReached}
        >
          Add
        </button>
      </div>
      <div className="row" style={{ justifyContent: "space-between", fontSize: 11 }}>
        <span className="muted">
          {value.length}/{maxOptions} options · {maxOptionLength} char max each
        </span>
      </div>
    </div>
  );
}
